import {
  marketplaceFulfillmentStatusLabel,
  type MarketplaceFulfillmentStatus,
} from "@nexus/contracts";
import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";

interface AdminOrdersPipelineWidgetProps {
  counts: Record<MarketplaceFulfillmentStatus, number>;
  activeStatuses: MarketplaceFulfillmentStatus[];
  onSelect: (status: MarketplaceFulfillmentStatus) => void;
}

const PIPELINE_STAGES = [
  {
    status: "SCRIPT",
    labelKey: "marketplace.ordersDashboard.overview.stages.notStarted",
    label: marketplaceFulfillmentStatusLabel("SCRIPT"),
    color: "var(--muted-foreground)",
  },
  {
    status: "SENT_TO_PRODUCTION",
    labelKey: "marketplace.ordersDashboard.overview.stages.production",
    label: marketplaceFulfillmentStatusLabel("SENT_TO_PRODUCTION"),
    color: "var(--chart-blue)",
  },
  {
    status: "SUBMITTED_FOR_FEEDBACK",
    labelKey: "marketplace.ordersDashboard.overview.stages.feedback",
    label: marketplaceFulfillmentStatusLabel("SUBMITTED_FOR_FEEDBACK"),
    color: "var(--chart-teal)",
  },
  {
    status: "SENT_FOR_REVISION",
    labelKey: "marketplace.ordersDashboard.overview.stages.revision",
    label: marketplaceFulfillmentStatusLabel("SENT_FOR_REVISION"),
    color: "var(--chart-orange)",
  },
  {
    status: "SUBMITTED_FOR_FINAL_APPROVAL",
    labelKey: "marketplace.ordersDashboard.overview.stages.approval",
    label: marketplaceFulfillmentStatusLabel("SUBMITTED_FOR_FINAL_APPROVAL"),
    color: "var(--chart-purple)",
  },
] as const satisfies ReadonlyArray<{
  status: MarketplaceFulfillmentStatus;
  labelKey: string;
  label: string;
  color: string;
}>;

export function AdminOrdersPipelineWidget({
  counts,
  activeStatuses,
  onSelect,
}: AdminOrdersPipelineWidgetProps) {
  const { t, i18n } = useTranslation();
  const total = PIPELINE_STAGES.reduce(
    (sum, stage) => sum + counts[stage.status],
    0,
  );
  const formatter = new Intl.NumberFormat(i18n.resolvedLanguage ?? i18n.language);

  return (
    <section aria-labelledby="admin-orders-pipeline-title" className="min-w-0 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2
          id="admin-orders-pipeline-title"
          className="text-xs font-medium text-foreground"
        >
          {t(
            "marketplace.ordersDashboard.overview.pipeline.title",
            "Script pipeline",
          )}
        </h2>
        <span className="text-caption tabular-nums text-muted-foreground">
          {t("marketplace.ordersDashboard.overview.scriptCount", {
            count: total,
            defaultValue_one: "{{count}} open script",
            defaultValue_other: "{{count}} open scripts",
          })}
        </span>
      </div>

      <div
        className="mt-3 flex h-2.5 overflow-hidden rounded-sm bg-muted"
        aria-hidden
      >
        {PIPELINE_STAGES.filter((stage) => counts[stage.status] > 0).map(
          (stage) => (
            <span
              key={stage.status}
              style={{
                backgroundColor: stage.color,
                width: `${(counts[stage.status] / total) * 100}%`,
              }}
            />
          ),
        )}
      </div>

      <ul className="mt-2 grid grid-cols-2 gap-x-2">
        {PIPELINE_STAGES.map((stage) => {
          const isActive =
            activeStatuses.length === 1 &&
            activeStatuses[0] === stage.status;
          const label = t(stage.labelKey, stage.label);
          return (
            <li key={stage.status}>
              <button
                type="button"
                aria-pressed={isActive}
                onClick={() => onSelect(stage.status)}
                className={cn(
                  "flex min-h-11 w-full cursor-pointer items-center gap-2 rounded-sm px-1.5 text-left text-caption outline-none transition-colors duration-150",
                  "hover:bg-foreground/5 focus-visible:ring-1 focus-visible:ring-ring motion-reduce:transition-none",
                  isActive &&
                    "bg-primary/5 text-primary dark:bg-primary/15",
                )}
              >
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-sm"
                  style={{ backgroundColor: stage.color }}
                />
                <span className="min-w-0 flex-1 truncate text-muted-foreground">
                  {label}
                </span>
                <span className="tabular-nums text-foreground">
                  {formatter.format(counts[stage.status])}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
